import { getAuctions } from "@/features/auctions/api";
import type { AuctionItem } from "@/features/auctions/types";
import { normalizeDrop } from "@/features/drops/normalize";
import type { Drop } from "@/features/drops/types";
import { getGalleries } from "@/features/galleries/api";
import type { GalleriesApiItem } from "@/features/galleries/types";
import { searchGlobal, type SearchResultArtist, type SearchResultCollection, type SearchResultNFT } from "@/features/search/api";
import { dropService } from "@/services/drop.service";
import { SEARCH_KINDS, type SearchKind } from "./kinds";

export type SearchBlog = Record<string, unknown> & { id: number | string; title?: string; slug?: string; cover?: string | null; mini_cover?: string | null };

export type SearchResults = {
  user: SearchResultArtist[];
  collection: SearchResultCollection[];
  nft: SearchResultNFT[];
  drops: Drop[];
  auctions: AuctionItem[];
  galleries: GalleriesApiItem[];
  blogs: SearchBlog[];
};

type Lang = "fa" | "en";
export type SearchSource = { id: string; kinds: readonly SearchKind[]; run: (text: string, lang: Lang, count: number) => Promise<Partial<SearchResults>> };

/**
 * WHERE EACH KIND IS SEARCHED — four sources, asked in parallel, each
 * rendered as soon as it answers and each allowed to fail alone.
 *
 *  - users, collections, NFTs and blogs: one `POST /search` — its contract.
 *  - drops: `/drop?name=`. Not the search endpoint's `drops` model, which
 *    answers 500 for the WHOLE request whenever a drop matches (measured:
 *    "activity" → 500, "xyzqq" → 200 empty) — asking for drops there would
 *    sink the other four kinds with it.
 *  - auctions: `/auctions?title=`; galleries: `/galleries?name=`. `/search`
 *    has no model for either (422).
 */
export const SEARCH_SOURCES: readonly SearchSource[] = [
  {
    id: "core",
    kinds: ["user", "collection", "nft", "blogs"],
    run: async (text, _lang, count) => {
      const { data } = await searchGlobal({ text, count, model: ["user", "collection", "nft", "blogs"] });
      return { user: data?.artists ?? [], collection: data?.collections ?? [], nft: data?.nfts ?? [], blogs: (data?.blogs ?? []) as SearchBlog[] };
    },
  },
  {
    id: "drops",
    kinds: ["drops"],
    run: async (text, lang, count) => {
      const res = await dropService.getDrops({ name: text, limit: count }, lang);
      // getDrops answers a failure with an empty list; say it failed instead.
      if (res?.message === "offline_fallback") throw new Error("drops unavailable");
      return { drops: ((res?.data?.drops ?? []) as unknown[]).map((d) => normalizeDrop(d, lang)) };
    },
  },
  { id: "auctions", kinds: ["auctions"], run: async (text, lang, count) => ({ auctions: (await getAuctions({ title: text, limit: count, lang })).data.items ?? [] }) },
  { id: "galleries", kinds: ["galleries"], run: async (text, _lang, count) => ({ galleries: (await getGalleries({ name: text, limit: count })).galleries ?? [] }) },
];

/** Every kind present, empty where its source has not answered (or failed). */
export function mergeResults(parts: readonly (Partial<SearchResults> | undefined)[]): SearchResults {
  const out = Object.fromEntries(SEARCH_KINDS.map((k) => [k, []])) as unknown as SearchResults;
  for (const part of parts) if (part) Object.assign(out, part);
  return out;
}

export const totalOf = (r: SearchResults | undefined) => (r ? SEARCH_KINDS.reduce((n, k) => n + r[k].length, 0) : 0);
